import { isAfter, isBefore, isEqual, parseISO } from 'date-fns';
import dateFnsTz from 'date-fns-tz';
import { go, pluck, filter, map, compact } from 'fxjs/es';

/**
 * 판매기간이 맞는지 체크
 * @param start_date
 * @param end_date
 * @returns {boolean}
 */
const { utcToZonedTime } = dateFnsTz;

function isCanSellingDate(start_date, end_date) {
  // 둘 다 null인 경우
  if (!start_date && !end_date) {
    return true;
  }

  const time_zone = 'Asia/Seoul';
  const now_utc = new Date();
  const now_kst = utcToZonedTime(now_utc, time_zone);
  /* 현재 날짜 밑으로는 00으로 맞추기 한국시간이니 9시로 맞추기 */
  now_kst.setHours(9, 0, 0, 0);
  const parsed_start = start_date
    ? utcToZonedTime(typeof start_date === 'string' ? parseISO(start_date) : start_date, time_zone)
    : null;

  const parsed_end = end_date
    ? utcToZonedTime(typeof end_date === 'string' ? parseISO(end_date) : end_date, time_zone)
    : null;

  // start_date만 있는 경우

  if (parsed_start && !parsed_end) {
    return isAfter(now_kst, parsed_start) || isEqual(now_kst, parsed_start);
  }

  // end_date만 있는 경우
  if (!parsed_start && parsed_end) {
    return isBefore(now_kst, parsed_end) || isEqual(now_kst, parsed_end);
  }

  // 둘 다 있는 경우
  const is_on_start = isAfter(now_kst, parsed_start) || isEqual(now_kst, parsed_start);
  const is_on_end = isBefore(now_kst, parsed_end) || isEqual(now_kst, parsed_end);

  return is_on_start && is_on_end;
}

/**
 * 사용할 products_mcon_themes_images 받아 해당 이미지와 테마가 판매 가능한 상태인지 체크
 * 이미지의 삭제 여부
 * 이미지의 테마가 판매 가능한 상태인지 체크
 * 크리에이터샵이 정상적인 운영 상태인지 체크
 *   테마 삭제 상태 1000
 *   크리에이터샵 운영중지 상태 2000
 *   테마 상태가 "OPEN" 이 아니면 3000
 *   테마 상태가 "OPEN" 이면서 판매기간이 맞는지 체크 4000
 *   해당 이미지들 중 삭제된 이미지가 있는지 체크 5000
 *   그 외 9000
 * @param products_mcon_themes_images
 * @param mcon_theme
 * @returns {{result: boolean, code: string, deleted_image_ids: number[]}}
 */

export function checkCanSellingMconTheme({ products_mcon_themes_images, mcon_theme }, _en) {
  const store = mcon_theme?._?.store;
  const image_ids = go(
    products_mcon_themes_images,
    map((products_mcon_themes_image) => products_mcon_themes_image.image_id),
  );
  if (_en !== '') {
    return {
      result: false,
      code: '10000',
      deleted_image_ids: image_ids,
    };
  }
  if (mcon_theme.deleted_at != null) {
    // 테마가 삭제된 상태
    return {
      result: false,
      code: '1000',
      deleted_image_ids: image_ids,
    };
  } else if (!store || store.suspend_store || !store.can_selling || store.is_hidden) {
    // 크리에이터샵 운영중지 상태
    return {
      result: false,
      code: '2000',
      deleted_image_ids: image_ids,
    };
  } else if (mcon_theme.status != 'OPEN') {
    // 공개 상태가 아닌 경우
    return {
      result: false,
      code: '3000',
      deleted_image_ids: image_ids,
    };
  } else if (mcon_theme.status == 'OPEN' && !isCanSellingDate(mcon_theme.start_date, mcon_theme.end_date)) {
    // 공개 상태이지만 판매 기간이 현재 시간과 맞지 않은 경우
    return {
      result: false,
      code: '4000',
      deleted_image_ids: image_ids,
    };
  }
  const image_ids_to_delete = go(
    products_mcon_themes_images,
    map((products_mcon_themes_image) => products_mcon_themes_image?._?.image),
    compact,
    filter((image) => {
      return image?.is_hidden === true || image?.is_public === false;
    }),
    pluck('id'),
  );

  if (image_ids_to_delete.length) {
    // 이미지가 삭제 또는 비공개된 경우
    return {
      result: false,
      code: '5000',
      deleted_image_ids: image_ids_to_delete,
    };
  }

  if (mcon_theme.status == 'OPEN' && isCanSellingDate(mcon_theme.start_date, mcon_theme.end_date)) {
    return {
      result: true,
    };
  } else {
    // 여기 들어오는건 다시 체크 해보기
    return {
      result: false,
      code: '9000',
      deleted_image_ids: image_ids,
    };
  }
}
